import React, { useState, useEffect } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import FunctionDialog from './FunctionDialog';
import { theme } from '../../style/theme';
import { useDispatch, useSelector } from 'react-redux';
import {
  setNodesForWorkflow,
  setEdgesForWorkflow,
} from '../../state/slices/mappingSlice';
import { useParams } from 'react-router-dom';
import { functionList } from './FunctionToolbar';

const FunctionNode = ({ id, data, selected, updatedConfig }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [isHandleConnected, setIsHandleConnected] = useState(false);
  const [selectedConnections, setSelectedConnections] = useState([]);
  const { workflowName: workflowId } = useParams();

  const functionInfo = functionList.find((f) => f.id === data.functionType);
  const icon = functionInfo?.icon || null;
  const displayLabel = functionInfo?.label || data.label || data.functionType;

  const { getEdges, getNodes } = useReactFlow();

  const dispatch = useDispatch();
  // const nodes = useSelector((state) => state.mapping.nodesGlobal);
  // const edges = useSelector((state) => state.mapping.edgesGlobal);

  const { nodes = [], edges = [] } = useSelector(
    (state) => state.mapping.mappingsByWorkflow[workflowId] || {},
  );

 // helper: build all source-node connections from updatedConfig
const getAllSourceConnections = (cfg) => {
  if (!cfg?.nodes) return [];
  const sourceNodes = cfg.nodes.filter(
    (node) => node?.type === 'sourceNode' || node?.config?.type === 'sourceNode' || node?.data?.type === 'sourceNode'
  );

  return sourceNodes.map((node) => ({
    nodeId: node.id || '',
    connName:
      node.data?.config?.connection?.conn_name ??
      node.config?.connection?.conn_name ??
      '',
    connId:
      node.data?.config?.connection?.conn_id ??
      node.config?.connection?.conn_id ??
      '',
  }));
};

useEffect(() => {
  // no immediate set here; we’ll set after we know incoming edges below
}, [updatedConfig]);

  const handleDeleteNode = () => {
    dispatch(
      setNodesForWorkflow({
        workflowId,
        nodes: nodes.filter((n) => n.id !== id),
      }),
    );
    dispatch(
      setEdgesForWorkflow({
        workflowId,
        edges: edges.filter((e) => e.source !== id && e.target !== id),
      }),
    );
  };

  useEffect(() => {
    const edges = getEdges();
    const nodes = getNodes();

    const incomingEdges = edges.filter((e) => e.target === id);
    const outgoingEdges = edges.filter((e) => e.source === id);

    const hasValidSource = incomingEdges.some((e) => {
      const sourceType = nodes.find((n) => n.id === e.source)?.type;
      return sourceType === 'sourceNode' || sourceType === 'functionNode';
    });

    const hasValidDestination = outgoingEdges.some((e) => {
      const targetType = nodes.find((n) => n.id === e.target)?.type;
      return targetType === 'functionNode' || targetType === 'destinationNode';
    });

    if (!hasValidSource || !hasValidDestination) {
      setIsHandleConnected(false);
      setSelectedFields([]);
      return;
    }

    console.log('conn Incoming edges:', incomingEdges);
     // --- NEW: filter connections by incomingEdges.source ---
  const allConnections = getAllSourceConnections(updatedConfig);
  const incomingSourceIds = new Set(incomingEdges.map((e) => e.source));

  // Keep only connections whose nodeId is one of the incoming sources
  const filteredBySources = allConnections.filter((c) =>
    incomingSourceIds.has(c.nodeId)
  );

  // Dedupe by connId AFTER filtering (so shared connections show up once if any linked source is connected)
  const uniqueConnections = Array.from(
    new Map(filteredBySources.map((c) => [c.connId, c])).values()
  );

  setSelectedConnections(uniqueConnections);
  console.log('connections for this functionNode:', uniqueConnections);

    const findSourceFieldLabelWithEntity = (edge) => {
      let currentEdge = edge;

      while (currentEdge) {
        const sourceNode = nodes.find((n) => n.id === currentEdge.source);
        if (!sourceNode) break;

        const sourceHandleId = currentEdge.sourceHandle?.split('--')[0];

        if (sourceNode.type === 'sourceNode') {
          for (const group of sourceNode.data.subFields || []) {
            const match = group.children.find((f) => f.id === sourceHandleId);
            console.log('match:', match);
            if (match) {
              return {
                label: match.label || match.id,
                entity: group.label || 'Unknown Entity',
                fieldName: match.id,
              };
            }
          }
          break;
        }

        // Move to the previous edge
        currentEdge = edges.find((e) => e.target === sourceNode.id);
      }

      return null;
    };
 
    const matchedLabelsWithEntities = incomingEdges
      .map(findSourceFieldLabelWithEntity) // returns { label, entity }
      .filter(Boolean);

    // ✅ Extract only labels (strings)
    const matchedLabels = matchedLabelsWithEntities.map((f) => f.label);

    // ✅ Set only labels into selectedFields (no objects!)
    setSelectedFields(matchedLabels);

    // ✅ If needed, keep entities separately
    setSelectedEntities([
      ...new Set(matchedLabelsWithEntities.map((f) => f.entity)),
    ]);

    setIsHandleConnected(true);
    // setSelectedFields(matchedLabels);
  }, [dialogOpen, getEdges, getNodes, id]);

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);
  const handleToastClose = () => setToastOpen(false);

  const handleSave = (newConfig, updatedFunctionType) => {
    console.log('updated function type:', updatedFunctionType);
    console.log('updated newConfig:', newConfig);
    console.log('updated function:', {
      function: updatedFunctionType,
      config: newConfig,
    });
    dispatch(
      setNodesForWorkflow({
        workflowId,
        nodes: nodes.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  config: newConfig,
                  functionType: updatedFunctionType || data.functionType,
                },
              }
            : node,
        ),
      }),
    );

    setToastOpen(true);
    handleCloseDialog();
  };

  return (
    <Box
      onDoubleClick={handleOpenDialog}
      sx={{
        borderRadius: '12px',
        padding: 1.5,
        backgroundColor: '#E5EFFB',
        minWidth: 45,
        textAlign: 'center',
        position: 'relative',
        cursor: 'pointer',
        transition: 'box-shadow 0.3s ease, transform 0.2s ease',

        boxShadow: selected
          ? '0px 4px 12px rgba(25, 118, 210, 0.35)' // Highlighted blue shadow
          : '0px 2px 6px rgba(0, 0, 0, 0.08)', // Subtle base shadow

        '&:hover': {
          boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.15)',
          transform: 'translateY(-2px)', // Subtle lift on hover
        },
      }}
    >
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          // data?.deleteNode?.(id);
          handleDeleteNode();
        }}
        sx={{
          position: 'absolute',
          top: '-4px',
          right: '-4px',
          backgroundColor: '#fff',
          color: theme.palette.error.main,
          width: '1.1rem',
          height: '1.1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            backgroundColor: theme.palette.error.dark,
            color: '#fff',
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Handle
        type="target"
        position={Position.Left}
        id={data.functionType === 'aggregation' ? 'input' : undefined}
        style={{
          background: '#0154D2',
          width: 8,
          height: 8,
          top: '50%',
        }}
      />

      <Tooltip title={data.functionType} arrow>
        <Box display="flex" flexDirection="column" alignItems="center">
          {icon ? (
            <img
              src={icon}
              alt={data.functionType}
              style={{
                width: 26,
                height: 26,
                objectFit: 'contain',
                marginBottom: 4,
              }}
            />
          ) : (
            <Typography variant="body2" fontWeight="bold">
              {/* {data.label} */}
              {displayLabel}
            </Typography>
          )}
        </Box>
      </Tooltip>

      <Handle
        type="source"
        position={Position.Right}
        id={data.functionType === 'aggregation' ? 'output' : undefined}
        style={{
          background: '#02296C',
          width: 8,
          height: 8,
          top: '50%',
        }}
      />

      <FunctionDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        functionType={data.functionType}
        configData={data.config}
        onSave={handleSave}
        icon={data.icon}
        selectedFields={selectedFields}
        selectedConnections={selectedConnections}
        selectedEntities={selectedEntities}
        isHandleConnected={isHandleConnected}
        workflowId={workflowId}
        updatedConfig={updatedConfig}
      />

      <Snackbar
        open={toastOpen}
        autoHideDuration={2000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleToastClose}
          severity="success"
          sx={{ width: '100%' }}
        >
          Function updated successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

FunctionNode.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({
    label: PropTypes.string,
    functionType: PropTypes.string,
    icon: PropTypes.string,
    config: PropTypes.object,
    onFunctionUpdate: PropTypes.func,
    deleteNode: PropTypes.func,
    id: PropTypes.string,
  }).isRequired,
  selected: PropTypes.bool,
  updatedConfig: PropTypes.object,
};

export default FunctionNode;
